import cn from 'classnames';
import { Field } from 'formik';
import { useRef } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import Tooltip from 'components/common/Tooltip';
import CaretRight from 'components/icons/navigation/CaretRight';
import CloseX from 'components/icons/navigation/CloseX';
import { TEMPO } from 'context/BrandContext';
import formLabel from 'helpers/formLabel';
import useBrand from 'hooks/useBrand';

import Error from './Error';

const TextField = ({
  buttonStackedMobile = false,
  displayErrorMessage = true,
  clearButton = false,
  clearButtonFunc = () => {},
  containerClass = '',
  error = '',
  fieldClass = '',
  helperText = '',
  label = '',
  labelClass = '',
  labelTooltip = false,
  leadingContent = '',
  onBlur = () => {},
  onFocus = () => {},
  placeholder = '',
  prefixBox = '',
  submitButton = false,
  submitButtonText = '',
  trailingContent = '',
  transitionGroupComponent = 'div',
  type = 'text',
  value = '',
  innerRef,
  isReferral = false,
  labelIcon,
  labelIconToolTip,
  maxLength,
  name,
  autoComplete,
}) => {
  const helperRef = useRef();
  const errorRef = useRef();
  const isTempo = useBrand(TEMPO);

  return (
    <div className={containerClass}>
      <label htmlFor={name} className={cn('textMd mb-2 flex leading-tight', labelClass)}>
        {isReferral ? '' : label || formLabel(name)}
        {labelTooltip && (
          <Tooltip ariaId={name} containerClass="ml-2">
            <p className="text-charcoal-90">{labelTooltip}</p>
          </Tooltip>
        )}
        {labelIcon && labelIconToolTip && (
          <Tooltip ariaId={`${name}-icon`} position="top" triggerButtonContent={labelIcon}>
            <span className="text">{labelIconToolTip}</span>
          </Tooltip>
        )}
      </label>
      <div
        className={cn('flex', {
          'flex-nowrap': submitButton && !buttonStackedMobile,
          'flex-col bp1:flex-row': submitButton && buttonStackedMobile,
          'border-red-100': Boolean(error),
        })}
      >
        <div className="relative grow text-center">
          {Boolean(leadingContent) && (
            <span
              className={cn(
                'flex',
                'flex-col',
                'justify-center',
                'items-center',
                'min-w-10',
                'absolute',
                'inset-y-px',
                'left-px',
                'font-normal',
                'px-3',
                {
                  'rounded-l bg-charcoal-50 text-charcoal-100':
                    prefixBox && Boolean(leadingContent),
                  'text-charcoal-80': !prefixBox,
                }
              )}
            >
              {leadingContent}
            </span>
          )}
          <Field
            id={name || placeholder}
            name={name}
            type={type}
            className={cn('textInput', fieldClass, {
              tempo: isTempo,
              'pl-4': !leadingContent,
              'pl-10': Boolean(leadingContent),
              'pl-14': prefixBox && Boolean(leadingContent),
              'pr-10': clearButton,
              'border-red-100': Boolean(error),
              'rounded-r-none': submitButton && !buttonStackedMobile,
              'rounded bp1:rounded-r-none': submitButton && buttonStackedMobile,
            })}
            placeholder={placeholder}
            onBlur={onBlur}
            onFocus={onFocus}
            innerRef={innerRef}
            maxLength={maxLength}
            autoComplete={autoComplete}
          />
          {Boolean(trailingContent) && trailingContent}
          {clearButton && value && (
            <button
              type="button"
              className="btnSecondary absolute inset-y-px right-px flex flex-col justify-center p-4 text-lg"
              onClick={clearButtonFunc}
            >
              <CloseX className="h-5 w-5" />
              <span className="sr-only">Clear field</span>
            </button>
          )}
        </div>
        {submitButton && (
          <button
            type="submit"
            className={cn('btn btnPrimary px-4', {
              'rounded-l-none py-2': submitButton && !buttonStackedMobile,
              'mt-4 rounded bp1:mt-0 bp1:rounded-l-none bp1:py-2':
                submitButton && buttonStackedMobile,
            })}
          >
            {submitButtonText || (
              <>
                <CaretRight className="iconMd text-white-100" />
                <span className="sr-only">Submit</span>
              </>
            )}
          </button>
        )}
      </div>
      <TransitionGroup
        className={`crossfadeContainer mt-1 min-h-${isReferral ? '3' : '6'}`}
        component={transitionGroupComponent}
      >
        {Boolean(helperText) && (
          <CSSTransition
            in={Boolean(helperText)}
            timeout={200}
            classNames="fadeInOut"
            nodeRef={helperRef}
          >
            <p ref={helperRef} className="textSm mb-1 font-normal text-charcoal-90">
              {helperText}
            </p>
          </CSSTransition>
        )}
        {Boolean(error) && Boolean(displayErrorMessage) && (
          <CSSTransition
            in={Boolean(error)}
            timeout={200}
            classNames="fadeInOut"
            nodeRef={errorRef}
          >
            <div ref={errorRef}>
              <Error message={error} className="crossfadeItem textSm mb-1 bg-white-100" />
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    </div>
  );
};

TextField.propTypes = {
  clearButton: PropTypes.bool,
  clearButtonFunc: PropTypes.func,
  containerClass: PropTypes.string,
  error: PropTypes.string,
  label: PropTypes.string,
  labelClass: PropTypes.string,
  labelIcon: PropTypes.node,
  labelIconToolTip: PropTypes.string,
  labelTooltip: PropTypes.bool,
  leadingContent: PropTypes.node,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  prefixBox: PropTypes.node,
  submitButton: PropTypes.bool,
  buttonStackedMobile: PropTypes.bool,
  submitButtonText: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.string,
  fieldClass: PropTypes.string,
  transitionGroupComponent: PropTypes.string,
  trailingContent: PropTypes.node,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  maxLength: PropTypes.number,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  displayErrorMessage: PropTypes.bool,
  helperText: PropTypes.string,
  isReferral: PropTypes.bool,
  autoComplete: PropTypes.string,
};

export default TextField;
